import { useState } from 'react';
import { Card, Button, Badge } from '@rackd/ui';
import { IconStar, IconTrash, IconUser, IconAlertTriangle } from '@tabler/icons-react';
import { formatDistanceToNow } from 'date-fns';
import type { Review } from '../api';

interface ReviewCardProps {
  review: Review;
  onDelete: (reviewId: string) => Promise<{ success: boolean }>;
  isDeleting?: boolean;
}

export function ReviewCard({ review, onDelete, isDeleting }: ReviewCardProps) {
  const [showConfirm, setShowConfirm] = useState(false);

  const handleDelete = async () => {
    const result = await onDelete(review.id);
    if (result.success) {
      setShowConfirm(false);
    }
  };

  // Check if review might need attention (low rating, short text, etc.)
  const needsAttention = review.rating <= 2 || (review.reviewText && review.reviewText.length < 10);

  return (
    <Card className={`p-4 ${needsAttention ? 'border-[var(--warning)]/30' : ''}`}>
      <div className="flex items-start gap-4">
        {/* User avatar */}
        <div className="w-10 h-10 rounded-full bg-[var(--background-tertiary)] flex items-center justify-center flex-shrink-0">
          <IconUser size={20} className="text-[var(--text-tertiary)]" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-[var(--text)]">
              {review.userDisplayName || 'Anonymous'}
            </span>
            <span className="text-sm text-[var(--text-tertiary)]">
              {review.userEmail || 'No email'}
            </span>
            {needsAttention && (
              <Badge className="bg-[var(--warning)]/10 text-[var(--warning)]">
                <IconAlertTriangle size={12} />
                Review
              </Badge>
            )}
          </div>

          {/* Program */}
          <p className="text-sm text-[var(--text-secondary)] mb-2">
            on <span className="font-medium">{review.programName}</span>
          </p>

          {/* Rating */}
          <div className="flex items-center gap-1 mb-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <IconStar
                key={star}
                size={16}
                className={star <= review.rating ? 'text-[var(--pr-gold)] fill-current' : 'text-[var(--border)]'}
              />
            ))}
            <span className="text-sm text-[var(--text-tertiary)] ml-1">
              {formatDistanceToNow(review.createdAt, { addSuffix: true })}
            </span>
          </div>

          {/* Review text */}
          {review.reviewText ? (
            <p className="text-sm text-[var(--text)] whitespace-pre-wrap">{review.reviewText}</p>
          ) : (
            <p className="text-sm text-[var(--text-tertiary)] italic">No review text</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex-shrink-0">
          {showConfirm ? (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowConfirm(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={handleDelete}
                loading={isDeleting}
              >
                Delete
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowConfirm(true)}
              className="text-[var(--text-tertiary)] hover:text-[var(--error)]"
            >
              <IconTrash size={18} />
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
